import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import * as Sentry from "@sentry/browser";
import { Amplify } from "aws-amplify";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import { Outlet, useSearchParams } from "react-router-dom";

import "./App.css";
import amplifyconfiguration from "./aws-exports";
import amplifyconfigurationDev from "./aws-exports-dev";
import LoadingSpinner from "./components/LoadingSpinner";
import LoginBackground from "./components/LoginBackground";
import Sidebar from "./components/Sidebar";
import Toast from "./components/Toast";
import Topbar from "./components/Topbar";
import { DestinationClientProvider } from "./contexts/ClientContext";
import SvgLogo from "./icons/LogoSQSmall";
import { getRefreshToken, getUserInfo, updateAmplifySession } from "./lib/auth";
import {
  getClientConfig,
  getClientId,
  refreshTokenWithRetry,
} from "./lib/automatic";
import {
  CLIENT_ID_PARAM,
  IS_LOCAL,
  SENTRY_TAG_CLIENT_ID,
  STAGE,
} from "./lib/constants";
// Import the ClientProvider and DestinationClientProvider
import {
  handleForbiddenError,
  handleInvalidClientIdError,
} from "./lib/errorHandler";

// Mutate the amplify redirect urls and domain - don't worry just mutate 🤪
amplifyconfiguration.oauth.redirectSignIn = IS_LOCAL
  ? "http://localhost:3002/"
  : `https://dashboard${STAGE === "prod" ? "" : `.${STAGE}`}.axle.insure/`;
amplifyconfiguration.oauth.redirectSignOut = IS_LOCAL
  ? "http://localhost:3002/"
  : `https://dashboard${STAGE === "prod" ? "" : `.${STAGE}`}.axle.insure/`;
amplifyconfiguration.oauth.domain = "auth.axle.insure";

switch (STAGE) {
  case "prod":
  case "sandbox":
    // Configure Amplify
    Amplify.configure(amplifyconfiguration);
    break;
  case "dev":
  default:
    amplifyconfigurationDev.oauth.redirectSignIn = IS_LOCAL
      ? "http://localhost:3002/"
      : `https://dashboard${STAGE === "prod" ? "" : `.${STAGE}`}.axle.insure/`;
    amplifyconfigurationDev.oauth.redirectSignOut = IS_LOCAL
      ? "http://localhost:3002/"
      : `https://dashboard${STAGE === "prod" ? "" : `.${STAGE}`}.axle.insure/`;

    Amplify.configure(amplifyconfigurationDev);
}

// Create context for the client
export const ClientContext = React.createContext({
  selectedClient: "",
  clientConfig: { id: "" },
  isPlatformUser: false,
  setSelectedClient: (clientId) => {},
  setClientConfig: (config) => {},
  setPlatformClientConfig: (config) => {},
  platformClientId: "",
  platformClientConfig: { id: "" },
  isLoading: true,
  isClientTransitioning: false,
  setClientTransitioning: (state) => {},
});
export const AdminContext = React.createContext({});

// ClientDataProvider component keeps track of the selected client in the dashboard and the platform client
const ClientDataProvider = ({ children }) => {
  // Initialize with proper default values
  const [clientConfig, setClientConfig] = useState({ id: "" });
  const [platformClientConfig, setPlatformClientConfig] = useState({ id: "" }); // Set the platform client config so we can show the display name in the dropdown
  const [selectedClient, setSelectedClient] = useState(""); // Currently selected client
  const [platformClientId, setPlatformClientId] = useState("");
  const [userRole, setUserRole] = useState("");
  const [isPlatformUser, setIsPlatformUser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isClientTransitioning, setClientTransitioning] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);

  // Function to handle token refresh and client config update
  const refreshClientToken = async (clientId) => {
    const currentRefreshToken = await getRefreshToken();
    const tokens = await refreshTokenWithRetry(clientId, currentRefreshToken);
    await updateAmplifySession(tokens);
  };

  // Function to revert client state on error
  const revertClientState = (previousClientId) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(CLIENT_ID_PARAM, previousClientId || "");
    setSearchParams(newParams);
  };

  // Function to safely change client with transition state
  const changeClient = async (newClientId) => {
    if (newClientId === selectedClient) return;

    setClientTransitioning(true);

    // First refresh the token to ensure we have proper authorization
    await refreshClientToken(newClientId);

    console.log(`in change client`);
    // Then get the client config with the new token
    const [configData, error] = await getClientConfig(
      ["validation", "ignition"],
      newClientId
    );
    if (error) {
      switch (get(error, "code")) {
        case 403:
          handleForbiddenError(error);
          break;
        case 400:
          handleInvalidClientIdError();
          break;
        default:
          console.error("Error fetching policy details:", error);
          break;
      }
      revertClientState(selectedClient);
      setClientTransitioning(false);
      return;
    }

    // Update client state and config in a single step
    setSelectedClient(newClientId);
    setClientConfig(configData);
    setClientTransitioning(false);
  };

  // Single useEffect to handle both URL sync and client config updates.
  // Guarded by isLoading so it doesn't race with loadInitialData on first mount.
  useEffect(() => {
    if (isLoading) return;

    const clientParam = searchParams.get(CLIENT_ID_PARAM);

    // If we have a client param and it's different from current client
    if (
      clientParam &&
      clientParam !== selectedClient &&
      !isClientTransitioning
    ) {
      changeClient(clientParam);
    }
    // If we have a selected client but no config, fetch it
    else if (
      selectedClient &&
      (!clientConfig || selectedClient !== clientConfig?.id) &&
      !isClientTransitioning
    ) {
      changeClient(selectedClient);
    }
  }, [searchParams, isClientTransitioning, isLoading]);

  // Initial useEffect to get the platform client and user role
  useEffect(() => {
    // Only run loadInitialData when user is authenticated
    if (authStatus !== "authenticated") {
      console.log(`auth status is not authenticated: `, authStatus);
      // Set loading to false so login page can be displayed
      setIsLoading(false);
      return;
    }

    async function loadInitialData() {
      setIsLoading(true); // Explicitly set loading to true at the start
      try {
        // Get user role and set platform user flag
        const userData = await getUserInfo();
        const currentRole = get(userData, "attributes['custom:role']");
        setUserRole(currentRole || "");

        const isPlatformRole =
          currentRole === "platform-admin" ||
          currentRole === "platform-member" ||
          currentRole === "platform-analyst" ||
          (currentRole && currentRole.includes("platform"));
        setIsPlatformUser(isPlatformRole);

        const clientParam = searchParams.get(CLIENT_ID_PARAM);
        // If URL has a client parameter, prioritize that
        if (clientParam) {
          console.log(
            "Initializing with client from URL parameter:",
            clientParam
          );
          const currentRefreshToken = await getRefreshToken();

          // Wait for token refresh to complete
          const tokens = await refreshTokenWithRetry(
            clientParam,
            currentRefreshToken
          );
          // Update current session with new tokens
          await updateAmplifySession(tokens);
          setSelectedClient(clientParam);

          const [configData, error] = await getClientConfig(
            ["validation", "ignition"],
            clientParam
          );
          if (error) {
            switch (get(error, "code")) {
              case 403:
                handleForbiddenError(error);
                break;
              case 400:
                handleInvalidClientIdError();
                break;
              default:
                console.error("Error fetching policy details:", error);
                break;
            }
            setIsLoading(false);
            return;
          }
          setClientConfig(configData);

          if (isPlatformRole) {
            // Get default platform client ID from API
            const [platformId, getClientIdError] = await getClientId();
            if (getClientIdError) {
              handleInvalidClientIdError();
              setIsLoading(false);
              return;
            }
            setPlatformClientId(platformId);

            console.log(`getting platform client config bc isPlatformRole`);
            // Store the platform client config separately
            const [platformConfig, error] = await getClientConfig(
              ["validation", "ignition"],
              platformId
            );
            if (error) {
              switch (get(error, "code")) {
                case 403:
                  handleForbiddenError(error);
                  break;
                case 400:
                  handleInvalidClientIdError();
                  break;
                default:
                  console.error("Error fetching policy details:", error);
                  break;
              }
              setIsLoading(false);
              return;
            }
            setPlatformClientConfig(platformConfig);
          }
        }
        // No client parameter, use the default client ID
        else {
          const [clientId, getClientIdError] = await getClientId();
          if (getClientIdError) {
            handleInvalidClientIdError();
            setIsLoading(false);
            return;
          }
          setSelectedClient(clientId);

          const [configData, error] = await getClientConfig(
            ["validation", "ignition"],
            clientId
          );
          if (error) {
            switch (get(error, "code")) {
              case 403:
                handleForbiddenError(error);
                break;
              case 400:
                handleInvalidClientIdError();
                break;
              default:
                console.error("Error fetching policy details:", error);
                break;
            }
            setIsLoading(false);
            return;
          }
          setClientConfig(configData);

          if (isPlatformRole) {
            console.log(`isPlatformRole in else block: `, isPlatformRole);
            setPlatformClientId(clientId);

            // For platform users, the initial client is also the platform client
            setPlatformClientConfig(configData);
          }
        }

        setIsLoading(false);
      } catch (error) {
        console.error(
          "Error loading initial data in ClientDataProvider:",
          error
        );
        setIsLoading(false); // Ensure loading is set to false on error
      }
    }

    loadInitialData();
  }, [authStatus]); // Run when authStatus changes - this ensures useEffect() runs on the login page AND after logging in

  // Tag Sentry events with the currently selected client so errors are attributable to a client
  useEffect(() => {
    if (selectedClient) {
      Sentry.setTag(SENTRY_TAG_CLIENT_ID, selectedClient);
    }
  }, [selectedClient]);

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <ClientContext.Provider
      value={{
        clientConfig,
        platformClientConfig,
        selectedClient,
        isPlatformUser,
        platformClientId,
        setSelectedClient: changeClient,
        setClientConfig,
        setPlatformClientConfig,
        isLoading,
        isClientTransitioning,
        setClientTransitioning,
      }}
    >
      <AdminContext.Provider value={userRole ?? {}}>
        {children}
      </AdminContext.Provider>
    </ClientContext.Provider>
  );
};

const Home = () => {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  console.log("authStatus", authStatus);
  // Consume context instead of managing local state for this data
  const { selectedClient, isLoading, isClientTransitioning } =
    React.useContext(ClientContext);

  // Show loading spinner while authentication is in progress or data is loading
  if (authStatus !== "authenticated" || (isLoading && !selectedClient)) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <div className="flex h-full w-full">
        <Sidebar />
        <div className="flex flex-1 flex-col ml-52 relative overflow-x-hidden">
          <Topbar />
          {isClientTransitioning ? (
            <div className="flex h-full w-full items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <Outlet />
          )}
          <Toaster position="bottom-right">
            {(t) => <Toast t={t} message={t.message} />}
          </Toaster>
        </div>
      </div>
    </>
  );
};

const Login = () => {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const isLoggingIn = authStatus === "configuring";

  return (
    <div className="flex flex-col items-center justify-center h-screen w-screen overflow-hidden">
      <div className="z-10 relative">
        {/* Logo positioned at the top center of the login card */}
        <div className="absolute left-1/2 top-0 transform -translate-x-1/2 -translate-y-1/2 z-20">
          <div className="h-12 w-12 rounded-full overflow-hidden shadow-[0_0_0_4px_white]">
            <SvgLogo />
          </div>
        </div>

        <div className="flex flex-col p-6 pt-10 pb-8 gap-8 border border-gray-300 rounded-lg bg-white w-96">
          {isLoggingIn ? (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : (
            <Authenticator
              loginMechanisms={["email"]}
              socialProviders={["google"]}
              hideSignUp={true}
              components={{
                SignIn: {
                  Header: () => (
                    <h1 className="text-xl font-semibold mb-10 text-center">
                      Sign in to your Axle account
                    </h1>
                  ),
                },
                ResetPassword: {
                  Header: () => (
                    <h1 className="text-xl font-semibold mb-4 text-center">
                      Reset password
                    </h1>
                  ),
                },
                ConfirmResetPassword: {
                  Header: () => (
                    <h1 className="text-xl font-semibold mb-4 text-center">
                      Set new password
                    </h1>
                  ),
                },
                ConfirmSignIn: {
                  Header: () => (
                    <h1 className="text-xl font-semibold mb-4 text-center">
                      Change password
                    </h1>
                  ),
                },
                ForceNewPassword: {
                  Header: () => (
                    <h1 className="text-xl font-semibold mb-4 text-center">
                      Change password
                    </h1>
                  ),
                },
              }}
            />
          )}
        </div>
      </div>
      <LoginBackground color="#f9fafb" h="64" />
    </div>
  );
};

function App() {
  const { route } = useAuthenticator((context) => [context.route]);
  return route === "authenticated" ? <Home /> : <Login />;
}

export default function AppWithProvider() {
  return (
    <Authenticator.Provider>
      <ClientDataProvider>
        {" "}
        <DestinationClientProvider>
          <App />
        </DestinationClientProvider>
      </ClientDataProvider>
    </Authenticator.Provider>
  );
}
